/**
 * ClickTracker — Tracks UI button clicks and sends analytics to the server.
 *
 * Hooks into EventBus to automatically track all emitted events that represent
 * user actions (button clicks, dialog opens, purchases, etc.).
 * Batches events and sends them every 30 seconds to minimize network overhead.
 */

import { EventBus } from './EventBus';

interface ClickEvent {
  event: string;
  timestamp: number;
  metadata?: string;
}

const BATCH_INTERVAL = 30_000; // 30 seconds
const MAX_BATCH_SIZE = 100;

// Events to track — these represent intentional user actions
const TRACKED_EVENTS = new Set([
  // HUD / Navigation
  'open_ship_store', 'open_component_marketplace', 'open_mineral_inventory',
  'open_auction_house', 'open_ship_progression', 'open_component_upgrade',
  'open-wreckage-dialog', 'open-enemy-profile', 'open-enemy-ship',
  'open-platform-management', 'open-station-management', 'open_alliance_dialog',
  'open_alliance_hq', 'open_browse_alliances', 'open_research_board',
  'open_relic_inventory', 'open_tech_list', 'open_crew_management',
  'open_insurance_dialog', 'open_withdraw_dialog', 'open_buy_isd_dialog',
  'open_refund_dialog', 'open_ship_design', 'open_build_menu',
  'open_how_to_guide', 'open_battle_report', 'open_profile_dialog',
  'open_direct_message', 'open_territory_map', 'open_ship_modification',
  'open_offline_recap', 'open_station_ship_buy',

  // Actions (user-initiated only, not automatic/tick-based)
  'mmo-action-move-to', 'mmo-action-attack-target', 'mmo-action-build',
  'mmo-action-tow', 'mmo-action-dock', 'mmo-action-undock',
  'mmo-action-follow', 'mmo-action-stop', 'mmo-action-repair',

  // Purchases
  'purchase_ship', 'purchase_component', 'purchase_isd',
  'wallet:connected', 'wallet:disconnected',

  // Social (user-typed chat only)
  'chat_message_sent', 'mmo_alliance_create', 'mmo_alliance_join',

  // Mining / Economy
  'start_mining', 'stop_mining', 'claim_loot', 'deliver_cargo',
  'auction_list', 'auction_buy', 'auction_cancel',
  'withdraw_request', 'refund_request',

  // Combat
  'activate_ability',

  // Settings
  'change_locale', 'toggle_audio', 'toggle_fullscreen',

  // FTUE / confusion signals
  'dialog_dismissed_quickly',  // emit when a dialog is closed within 2s of opening
  'error_shown',                // emit when an error toast appears
  'help_opened',                // emit when help/tutorial is opened
  'tutorial_step_completed',    // emit on each tutorial step
  'tutorial_skipped',           // emit if user dismisses the tutorial
  'first_login_session_started',
]);

class ClickTrackerService {
  private buffer: ClickEvent[] = [];
  private intervalId: ReturnType<typeof setInterval> | null = null;
  /**
   * Start tracking. Call once after auth.
   */
  init(): void {

    // Hook into EventBus — wrap the original emit
    const originalEmit = EventBus.emit.bind(EventBus);
    EventBus.emit = (event: string, ...args: any[]) => {
      if (TRACKED_EVENTS.has(event)) {
        this.track(event, args[0]);
      }
      return originalEmit(event, ...args);
    };

    // Start batch sender
    this.intervalId = setInterval(() => this.flush(), BATCH_INTERVAL);

    // Flush on page unload, plus signal session end so the server can close the
    // session immediately (otherwise it waits for the 30-min idle sweep).
    window.addEventListener('beforeunload', () => {
      this.flush();
      this.signalSessionEnd('beforeunload');
    });

    // visibilitychange catches mobile / tab-switch cases where beforeunload may not fire
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });

    console.log('[ClickTracker] Initialized, tracking', TRACKED_EVENTS.size, 'event types');
  }

  /**
   * Notify the server that the player session is ending (tab close / logout).
   * Uses sendBeacon so the request survives the page unload.
   */
  signalSessionEnd(reason: string): void {
    const token = localStorage.getItem('crimsonmandate_token');
    if (!token) return;
    try {
      const blob = new Blob([JSON.stringify({ reason })], { type: 'application/json' });
      // sendBeacon doesn't carry custom headers; the server route accepts the bearer
      // token via either Authorization header or query string. Fall back to fetch keepalive
      // when the bearer must travel as a header.
      if (navigator.sendBeacon) {
        // Encode token in the URL since sendBeacon can't set Authorization
        navigator.sendBeacon(`/api/analytics/session-end?token=${encodeURIComponent(token)}`, blob);
      } else {
        fetch('/api/analytics/session-end', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ reason }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch (_) { /* never block unload */ }
  }

  /**
   * Track a click event.
   */
  track(event: string, data?: any): void {
    const metadata = data ? JSON.stringify(data).slice(0, 200) : undefined;
    this.buffer.push({ event, timestamp: Date.now(), metadata });

    if (this.buffer.length >= MAX_BATCH_SIZE) {
      this.flush();
    }
  }

  /**
   * Send buffered events to the server.
   */
  flush(): void {
    if (this.buffer.length === 0) return;

    const events = this.buffer.splice(0, MAX_BATCH_SIZE);

    const token = localStorage.getItem('crimsonmandate_token');
    if (!token) return;

    fetch('/api/analytics/clicks', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ events }),
    }).catch(() => {
      // If send fails, put events back (up to max)
      if (this.buffer.length < MAX_BATCH_SIZE * 2) {
        this.buffer.unshift(...events);
      }
    });
  }

  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.flush();
  }
}

export const ClickTracker = new ClickTrackerService();
